
"use client";

import React, { useEffect, useRef } from 'react';
import { cn } from "@/lib/utils";

// Make SmilesDrawer available on the window object for TypeScript
declare global {
  interface Window {
    SmilesDrawer: {
      Version?: string;
      Drawer: new (options: any) => {
        draw: (
          tree: any,
          canvasElementOrId: string | HTMLCanvasElement,
          themeName?: string,
          darkBackground?: boolean
        ) => void;
      };
      Parser: {
        parse: ( // This is the synchronous parser from PEG.js
          smiles: string
        ) => any; // Returns the parsed tree or throws an error
        SyntaxError: any; // Constructor for syntax errors
      };
      apply?: (options?:any, selector?:string, themeName?:string, onError?:Function) => void;
    };
    SmiDrawer?: Window['SmilesDrawer'];
  }
}

interface SmilesDrawerRendererProps {
  smilesToDraw: string;
  drawingOptions?: Record<string, any>;
  className?: string;
}

// Constants for max and default sizes
// Modificar solo las constantes de tamaño máximo y por defecto
const MAX_SMILES_WIDTH = 200; // Reducido de 300
const MAX_SMILES_HEIGHT = 150; // Reducido de 225
const DEFAULT_SMILES_WIDTH = 150; // Reducido de 250
const DEFAULT_SMILES_HEIGHT = 100; // Reducido de 180

const SmilesDrawerRenderer: React.FC<SmilesDrawerRendererProps> = ({
  smilesToDraw,
  drawingOptions = {}, // Ensure drawingOptions is always an object
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Generate a unique ID for each canvas to avoid conflicts if multiple renderers are on a page.
  const canvasId = `smiles-canvas-${React.useId().replace(/:/g, "")}`;

  useEffect(() => {
    // console.log(`SDR DEBUG: useEffect TRIGGERED for canvasId: ${canvasId} SMILES: ${smilesToDraw}`);

    if (typeof window === 'undefined') {
      // console.warn("SDR WARN: window object not available (SSR or pre-hydration).");
      return;
    }

    if (!window.SmilesDrawer) {
      // console.warn("SDR WARN: SmilesDrawer library (window.SmilesDrawer) not loaded yet or not found on window object.");
      return;
    }
    // console.log("SDR DEBUG: SmilesDrawer library found on window object:", window.SmilesDrawer);
    
    const SmilesParser = window.SmilesDrawer.Parser;
    if (!SmilesParser || typeof SmilesParser.parse !== 'function') {
      // console.error("SDR ERROR: SmilesDrawer.Parser or SmilesDrawer.Parser.parse function not found. Parser object:", SmilesParser);
      return;
    }
    // console.log("SDR DEBUG: SmilesDrawer.Parser found:", SmilesParser);

    const SmilesDrawerConstructor = window.SmilesDrawer.Drawer;
    if (!SmilesDrawerConstructor || typeof SmilesDrawerConstructor !== 'function') {
        // console.error("SDR ERROR: SmilesDrawer.Drawer constructor not found on global object. Drawer constructor:", SmilesDrawerConstructor);
        return;
    }
    // console.log("SDR DEBUG: SmilesDrawer.Drawer constructor found:", SmilesDrawerConstructor);

    const canvasElement = canvasRef.current;
    if (!canvasElement) {
      // console.error("SDR ERROR: Canvas element not found for ID:", canvasId);
      return;
    }
    // console.log("SDR DEBUG: Canvas element found for ID:", canvasId, canvasElement);

    if (!smilesToDraw || typeof smilesToDraw !== 'string' || smilesToDraw.trim() === "") {
      // console.warn("SDR WARN: No valid SMILES string to draw:", smilesToDraw);
      const ctx = canvasElement.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
      }
      return;
    }
    
    // console.log("SDR DEBUG: Attempting to draw (SYNC PARSE). SMILES:", smilesToDraw, "Passed drawingOptions:", drawingOptions, "Canvas ID:", canvasId);

    try {
      // 1. Determine initial width and height
      let initialWidth = DEFAULT_SMILES_WIDTH;
      let initialHeight = DEFAULT_SMILES_HEIGHT;

      if (drawingOptions && typeof drawingOptions.width === 'number' && typeof drawingOptions.height === 'number') {
        initialWidth = drawingOptions.width;
        initialHeight = drawingOptions.height;
      }

      // 2. Apply maximum size constraint
      const finalCanvasWidth = Math.min(initialWidth, MAX_SMILES_WIDTH);
      const finalCanvasHeight = Math.min(initialHeight, MAX_SMILES_HEIGHT);
      
      // Options for the SmilesDrawer.Drawer constructor
      // Pass other options like padding, theme, etc., through, but override width/height with our constrained values
      const constructorOptions = {
        ...drawingOptions, 
        width: finalCanvasWidth,
        height: finalCanvasHeight,
        terminalCarbons: true,
      };
      // console.log("SDR DEBUG: Effective constructorOptions for SmilesDrawer.Drawer:", constructorOptions);

      // Set canvas attributes based on final constrained options
      canvasElement.width = finalCanvasWidth;
      canvasElement.height = finalCanvasHeight;
      // console.log("SDR DEBUG: Canvas dimensions set. Width:", canvasElement.width, "Height:", canvasElement.height);
      
      const drawerInstance = new SmilesDrawerConstructor(constructorOptions);
      // console.log("SDR DEBUG: SmilesDrawer.Drawer instance created:", drawerInstance);

      let parsedTree;
      try {
        // console.log("SDR DEBUG: Calling SYNCHRONOUS SmilesDrawer.Parser.parse with SMILES:", smilesToDraw);
        parsedTree = SmilesParser.parse(smilesToDraw); // Synchronous call
        // console.log("SDR DEBUG: SMILES string parsed successfully (SYNC). Tree:", parsedTree);

        try {
          let themeName = 'light'; // Default theme
          if (typeof constructorOptions.theme === 'string') {
              themeName = constructorOptions.theme;
          }
          // console.log("SDR DEBUG: Calling drawerInstance.draw with tree, canvasElement, theme:", themeName);
          drawerInstance.draw(parsedTree, canvasElement, themeName, false); // false for darkBackground is default
          // console.log("SDR DEBUG: Draw call completed for SMILES:", smilesToDraw);
        } catch (drawError: any) {
          // console.error(
          //   "SDR ERROR: SmilesDrawer drawing error after successful parse. SMILES:", smilesToDraw,
          //   "Parsed Tree:", parsedTree,
          //   "Error Type:", drawError.name, "Message:", drawError.message, "Stack:", drawError.stack
          // );
          const ctx = canvasElement.getContext('2d');
          if (ctx) {
              ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
              ctx.font = "10px Arial";
              ctx.fillStyle = "red";
              ctx.textAlign = "center";
              ctx.fillText("Draw Error", canvasElement.width / 2, canvasElement.height / 2);
          }
        }
      } catch (parseError: any) {
        // console.error(
        //     "SDR ERROR: SmilesDrawer parsing error (SYNC). Input SMILES:", smilesToDraw,
        //     "Error Type:", parseError.name, "Message:", parseError.message, "Stack:", parseError.stack
        // );
        const ctx = canvasElement.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
            ctx.font = "10px Arial";
            ctx.fillStyle = "red";
            ctx.textAlign = "center";
            ctx.fillText("Parse Error", canvasElement.width / 2, canvasElement.height / 2);
        }
      }
    } catch (generalError: any) {
      // console.error(
      //   "SDR ERROR: General SmilesDrawer setup error. Input SMILES:", smilesToDraw,
      //   "Initial Drawing Options:", drawingOptions,
      //   "Error Type:", generalError.name, "Message:", generalError.message, "Stack:", generalError.stack
      // );
      if (canvasElement) {
          const ctx = canvasElement.getContext('2d');
          if (ctx) {
              ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
              ctx.font = "10px Arial";
              ctx.fillStyle = "red";
              ctx.textAlign = "center";
              ctx.fillText("Setup Error", canvasElement.width / 2, canvasElement.height / 2);
          }
      }
    }
  }, [smilesToDraw, drawingOptions, canvasId]); // Ensure drawingOptions is in dependency array

  return (
    <canvas
      ref={canvasRef}
      id={canvasId}
      className={cn("border border-muted rounded-md", className)}
      // Removing the temporary red border: style={{ border: '1px solid red' }} 
    ></canvas>
  );
};

export default SmilesDrawerRenderer;
